import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { FingerprintjsProAngularService } from '@fingerprintjs/fingerprintjs-pro-angular';
import { IntakePayloadSchema } from 'fido-types';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { catchError, lastValueFrom, map, tap, throwError } from 'rxjs';
import { z } from 'zod';
import { environment } from '../environments/environment';
import { IntakeTelemetry } from './intake-telemetry';
import { IntakeBootstrapResponseSchema, IntakePayload } from './model';

export const IntakeSessionResponseSchema = z.strictObject({
  sessionId: z.string(),
  firmSlug: z.string(),
  firmWebsiteUrl: z.string().nullish(),
  firmName: z.string(),
  draftData: IntakePayloadSchema.partial().nullable(),
  submission: IntakePayloadSchema.nullable(),
  status: z.string(),
  expiresAt: z.number(),
  includeSituations: z.boolean(),
  includeMatters: z.boolean(),
  includeQualifiers: z.boolean(),
  includeUrgency: z.boolean(),
});

export type IntakeSessionResponse = z.infer<typeof IntakeSessionResponseSchema>;

@Injectable({ providedIn: 'root' })
export class IntakeSession {
  private readonly http = inject(HttpClient);
  private readonly intakeTelemetry = inject(IntakeTelemetry);
  private readonly fingerprintService = inject(FingerprintjsProAngularService);
  private readonly sessionDataStorage = injectLocalStorage<IntakeSessionResponse | null>(
    'session-data',
  );

  private readonly tokenStorage = injectLocalStorage('session-token');

  private readonly bootstrapUrl = environment.intakeApiBaseUrl
    ? `${environment.intakeApiBaseUrl}/bootstrap`
    : '/intake/bootstrap';
  private readonly autosaveUrl = environment.intakeApiBaseUrl
    ? `${environment.intakeApiBaseUrl}/autosave`
    : '/intake/autosave';
  private readonly submitUrl = environment.intakeApiBaseUrl
    ? `${environment.intakeApiBaseUrl}/submit`
    : '/intake/submit';

  private readonly sessionUrl = environment.intakeApiBaseUrl
    ? `${environment.intakeApiBaseUrl}/session`
    : '/intake/session';

  readonly sessionExpiryTime = computed(() => this.sessionDataStorage()?.expiresAt ?? null);

  readonly submission = computed(() => this.sessionDataStorage()?.submission ?? null);

  readonly submitted = computed(
    () => this.sessionDataStorage()?.status === 'submitted' || !!this.submission(),
  );

  readonly firmWebsiteUrl = computed(() => this.sessionDataStorage()?.firmWebsiteUrl ?? null);

  readonly externalOrigin = computed(() => sessionStorage.getItem('external_origin') || null);

  readonly firmSlug = computed(() => this.sessionDataStorage()?.firmSlug ?? null);

  readonly firmName = computed(() => this.sessionDataStorage()?.firmName ?? null);

  readonly sessionExpired = computed(() => {
    const expiryTime = this.sessionExpiryTime();
    return expiryTime ? Date.now() >= expiryTime : false;
  });

  readonly draft = computed(() => this.sessionDataStorage()?.draftData ?? null);

  readonly includeSituations = computed(() => this.sessionDataStorage()?.includeSituations ?? true);

  readonly includeMatters = computed(() => this.sessionDataStorage()?.includeMatters ?? true);

  readonly includeQualifiers = computed(() => this.sessionDataStorage()?.includeQualifiers ?? true);

  readonly includeUrgency = computed(() => this.sessionDataStorage()?.includeUrgency ?? true);

  readonly hasToken = computed(() => !!this.tokenStorage());

  /** True while a bootstrap HTTP call is in-flight. */
  readonly bootstrapping = signal(false);

  /**
   * Resolves once the current bootstrap call completes (success or failure).
   * If no bootstrap is in-flight, resolves immediately.
   * Autosave awaits this so it never fires with a null/stale token.
   */
  private bootstrapPromise: Promise<void> = Promise.resolve();

  readonly ready = computed(() => this.sessionDataStorage() !== null);

  readonly nextIncompleteStep = computed(() => {
    const draft = this.draft();

    if (this.submitted()) return '/confirmation';

    if (!draft) return '/';

    if (!draft.urgency) return '/urgency';
    if (!draft.name || !draft.email || !draft.phone) return '/contact-info';

    return '/contact-info';
  });

  async bootstrap(firmSlug: string) {
    // Check for existing valid session
    const existingToken = this.tokenStorage();
    const expiryTime = this.sessionExpiryTime();

    if (existingToken && expiryTime && Date.now() < expiryTime) {
      // Session is valid, nothing to do
      return;
    }

    this.bootstrapping.set(true);
    this.bootstrapPromise = this.createSession(firmSlug);
    return this.bootstrapPromise;
  }

  private async createSession(firmSlug: string) {
    try {
      let visitorId: string | null = null;
      try {
        const fingerprint = await this.fingerprintService.getVisitorData({
          extendedResult: true,
        });
        visitorId = fingerprint.visitorId;
      } catch {
        // Fingerprint blocked or unavailable — proceed without it
      }
      const referrer = sessionStorage.getItem('external_origin') || document.referrer || null;
      await lastValueFrom(
        this.http.post(this.bootstrapUrl, { firmSlug, visitorId, referrer }).pipe(
          map((response) => IntakeBootstrapResponseSchema.parse(response)),
          tap((res) => {
            this.tokenStorage.set(res.token);
            this.sessionDataStorage.set({
              sessionId: res.sessionId,
              firmSlug: res.firmSlug,
              firmWebsiteUrl: res.firmWebsiteUrl,
              firmName: res.firmName,
              draftData: null,
              submission: null,
              status: 'active',
              expiresAt: res.expiresAt,
              includeSituations: res.includeSituations,
              includeMatters: res.includeMatters,
              includeQualifiers: res.includeQualifiers,
              includeUrgency: res.includeUrgency,
            });
          }),
        ),
      );
    } finally {
      this.bootstrapping.set(false);
    }
  }

  restoreSession() {
    const token = this.tokenStorage();
    if (!token) return null;

    return lastValueFrom(
      this.http.get(this.sessionUrl, { headers: { Authorization: `Bearer ${token}` } }).pipe(
        map((response) => IntakeSessionResponseSchema.parse(response)),
        tap((response) => this.sessionDataStorage.set(response)),
        catchError((error) => {
          this.intakeTelemetry.send('restore_session_failed', { error });
          return throwError(() => error);
        }),
      ),
    );
  }

  async autosave(data: Partial<IntakePayload>) {
    // Wait for any in-flight bootstrap to complete so we have a valid token.
    await this.bootstrapPromise;

    return lastValueFrom(
      this.http
        .post(this.autosaveUrl, data, {
          headers: {
            Authorization: `Bearer ${this.tokenStorage()}`,
          },
        })
        .pipe(
          tap(() => {
            const current = this.sessionDataStorage();
            if (!current) throw new Error('Cannot autosave: session data not initialized');

            this.sessionDataStorage.set({
              sessionId: current.sessionId,
              firmSlug: current.firmSlug,
              firmWebsiteUrl: current.firmWebsiteUrl,
              firmName: current.firmName,
              draftData: { ...current.draftData, ...data } as IntakeSessionResponse['draftData'],
              submission: current.submission,
              status: current.status,
              expiresAt: current.expiresAt,
              includeSituations: current.includeSituations,
              includeMatters: current.includeMatters,
              includeQualifiers: current.includeQualifiers,
              includeUrgency: current.includeUrgency,
            });

            this.intakeTelemetry.send('autosave_success', { data });
          }),
          catchError((error) => {
            this.intakeTelemetry.send('autosave_failed', { error });
            return throwError(() => error);
          }),
        ),
    );
  }

  submit(
    data: Required<Pick<IntakePayload, 'name' | 'phone' | 'email'>> &
      Pick<IntakePayload, 'referralSource'>,
  ) {
    return lastValueFrom(
      this.http
        .post(this.submitUrl, data, {
          headers: {
            Authorization: `Bearer ${this.tokenStorage()}`,
          },
        })
        .pipe(
          tap(() => this.intakeTelemetry.send('form_submitted', { data })),
          catchError((error) => {
            this.intakeTelemetry.send('form_submission_failed', { error });
            return throwError(() => error);
          }),
        ),
    );
  }

  async clearSession() {
    this.tokenStorage.set(null);
    this.sessionDataStorage.set(null);
    await this.intakeTelemetry.send('session_cleared');
  }

  async startOver() {
    await this.autosave({
      situation: null,
      matter: null,
      urgency: null,
      qualifiers: null,
      name: null,
      email: null,
      phone: null,
      referralSource: null,
    });
  }
}
